/**
 * component-cart.js
 * AJAX quantity updates, line removal, drawer refresh, and edit-in-cart.
 *
 * Everything degrades: the cart page is a real form that posts to /cart, and
 * the drawer's contents are server-rendered. This file removes page reloads,
 * it is not what makes the cart work.
 *
 * All markup comes back from Liquid via the Cart API's `sections` parameter —
 * nothing is templated client-side, so there is exactly one implementation of
 * how a cart line looks.
 */
(function () {
  'use strict';

  // ---------------------------------------------------------------------
  // Single-execution guard. NOT optional — see component-drawer.js.
  //
  // Loaded by BOTH cart-drawer.liquid and main-cart.liquid, so on /cart the
  // browser executes this file twice. Every document-level listener below
  // would be registered twice, and the consequences here are worse than a
  // drawer that will not open: a single quantity change or remove would fire
  // TWO /cart/change requests, and each 'cart:added' event would open the
  // drawer twice and re-fetch the cart twice.
  // ---------------------------------------------------------------------
  if (window.__themeCartLoaded) return;
  window.__themeCartLoaded = true;

  const drawer = document.getElementById('CartDrawer');
  const drawerSectionId = drawer ? drawer.dataset.sectionId : null;

  /* ------------------------------------------------------------------ *
   * Rendering
   * ------------------------------------------------------------------ */

  /**
   * Replaces cart markup from a Cart API response.
   * @param {Object} cart Parsed cart JSON, possibly with a `sections` map.
   */
  function applyCart(cart) {
    if (cart.sections && drawerSectionId && cart.sections[drawerSectionId]) {
      replaceDrawer(cart.sections[drawerSectionId]);
    }

    // The cart page is not a section, so it is refreshed by re-fetching the
    // page and swapping the two regions that can change.
    if (document.querySelector('[data-cart-page]')) refreshCartPage();

    window.Theme.emit('cart:updated', cart);
  }

  /**
   * Swaps the drawer's internals without replacing <drawer-element> itself —
   * that element owns the open state, the focus trap and the scroll lock, and
   * replacing it mid-interaction would drop all three.
   */
  function replaceDrawer(html) {
    if (!drawer) return;

    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const incoming = parsed.querySelector('.drawer__panel');
    const current = drawer.querySelector('.drawer__panel');
    if (!incoming || !current) return;

    const wasOpen = drawer.open;
    current.replaceWith(incoming);

    // `panel` is captured once in connectedCallback, so replacing the panel
    // element leaves drawer.panel pointing at a detached node.
    //
    // This re-point must happen UNCONDITIONALLY. It used to run only when the
    // drawer was already open, which missed the most common path of all: on
    // add-to-cart the drawer is CLOSED, so the 'cart:added' handler swaps the
    // panel first and calls show() second. show() then trapped focus on the
    // old, detached panel — focus stayed on <body>, the dialog was never
    // announced, and tabbing walked the page behind the open drawer instead.
    // Silent: no console error, and the drawer still looked correct.
    drawer.panel = drawer.querySelector('.drawer__panel') || drawer;

    // Only an already-open drawer needs focus moved here; show() does it
    // otherwise, and stealing focus mid-interaction would be worse.
    if (wasOpen) drawer.panel.focus({ preventScroll: true });
  }

  function refreshCartPage() {
    fetch(window.location.pathname, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then((response) => response.text())
      .then((html) => {
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        ['[data-cart-page]'].forEach((selector) => {
          const incoming = parsed.querySelector(selector);
          const current = document.querySelector(selector);
          if (incoming && current) current.replaceWith(incoming);
        });
      })
      .catch(() => window.location.reload());
  }

  function setBusy(busy) {
    document.querySelectorAll('[data-cart-items]').forEach((list) => {
      list.setAttribute('aria-busy', busy ? 'true' : 'false');
      list.classList.toggle('is-loading', busy);
    });
  }

  /* ------------------------------------------------------------------ *
   * Cart mutations
   * ------------------------------------------------------------------ */

  /**
   * @param {string} key Line item key.
   * @param {number} quantity 0 removes the line.
   */
  function changeLine(key, quantity) {
    setBusy(true);

    const body = { id: key, quantity: quantity };
    if (drawerSectionId) body.sections = drawerSectionId;

    return fetch(window.Theme.routes.cart_change, window.Theme.cartRequest(body))
      .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
      .then(({ ok, data }) => {
        if (!ok) throw new Error(data.description || data.message || window.Theme.strings.cartError);
        applyCart(data);
        return data;
      })
      .catch((error) => {
        window.Theme.announce(error.message);
        // The optimistic value in the input is now wrong. Re-render from the
        // server rather than guessing what it should have been.
        refreshFromServer();
        throw error;
      })
      .finally(() => setBusy(false));
  }

  /**
   * Re-reads the cart from the server and repaints. Used after a failed
   * mutation, when the optimistic value on screen is known to be wrong and
   * guessing the correct one would be worse than asking.
   */
  function refreshFromServer() {
    fetch(`${window.Theme.routes.cart}.js`, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then((response) => response.json())
      .then((cart) => {
        window.Theme.emit('cart:updated', cart);

        if (drawerSectionId) {
          return window.Theme
            .fetchSection(drawerSectionId, window.location.href)
            .then(replaceDrawer)
            .catch((error) => {
              if (error.name !== 'AbortError') throw error;
            });
        }
        return cart;
      })
      .catch(() => {});
  }

  /* ------------------------------------------------------------------ *
   * Events
   * ------------------------------------------------------------------ */

  // Quantity. Debounced because the +/- buttons are commonly tapped several
  // times in a row, and each tap would otherwise be its own request.
  const debouncedChange = window.Theme.debounce((key, quantity) => {
    changeLine(key, quantity).catch(() => {});
  }, 400);

  document.addEventListener('change', (event) => {
    const input = event.target.closest('[data-cart-quantity]');
    if (!input) return;

    const quantity = Math.max(0, parseInt(input.value, 10) || 0);
    debouncedChange(input.dataset.key, quantity);
  });

  document.addEventListener('click', (event) => {
    const remove = event.target.closest('[data-cart-remove]');
    if (!remove) return;

    event.preventDefault();

    // Focus is about to be destroyed with the line. Move it somewhere stable
    // first, or a keyboard user is dropped to the top of the document.
    const list = remove.closest('[data-cart-items]');
    const line = remove.closest('[data-cart-item]');
    const next = line ? line.nextElementSibling || line.previousElementSibling : null;

    changeLine(remove.dataset.key, 0)
      .then(() => {
        const target =
          (next && document.querySelector(`[data-key="${next.dataset.key}"] .cart-item__remove`)) ||
          (list && list.closest('.drawer__panel')) ||
          document.getElementById('MainContent');
        if (target && typeof target.focus === 'function') target.focus({ preventScroll: true });
      })
      .catch(() => {});
  });

  // Order note. Saved on blur rather than per keystroke.
  document.addEventListener('change', (event) => {
    const note = event.target.closest('[data-cart-note]');
    if (!note) return;

    fetch(window.Theme.routes.cart_update, window.Theme.cartRequest({ note: note.value })).catch(
      () => window.Theme.announce(window.Theme.strings.cartError)
    );
  });

  /* ------------------------------------------------------------------ *
   * Open the drawer when something is added
   * ------------------------------------------------------------------ */

  window.Theme.on('cart:added', (data) => {
    if (data.sections && drawerSectionId && data.sections[drawerSectionId]) {
      replaceDrawer(data.sections[drawerSectionId]);
    }

    if (drawer && typeof drawer.show === 'function') {
      // Pass the add-to-cart button through as the opener so closing the
      // drawer returns focus to it rather than to <body>.
      drawer.show(data.openerElement);
    } else {
      // No drawer configured — the merchant chose the cart page.
      window.location.href = window.Theme.routes.cart;
      return;
    }

    // Re-fetch the cart so the header count and any other subscriber get the
    // authoritative item_count, which the add response does not include.
    fetch(`${window.Theme.routes.cart}.js`, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then((response) => response.json())
      .then((cart) => window.Theme.emit('cart:updated', cart))
      .catch(() => {});
  });

  /* ------------------------------------------------------------------ *
   * Edit in cart
   *
   * "Edit" links back to the product with the original line's key in `edit`
   * and its properties in `p[…]`. On submit the replacement is added first and
   * the original removed only after that succeeds — doing it the other way
   * round loses the customer's configuration if the add fails.
   * ------------------------------------------------------------------ */

  const editKey = new URLSearchParams(window.location.search).get('edit');

  if (editKey) {
    const form = document.querySelector('[data-product-form]');

    if (form) {
      // Relabel the action so the page does not claim to be adding a second
      // copy of something the customer is amending.
      document.querySelectorAll('[data-add-label]').forEach((label) => {
        if (window.Theme.strings.updateItem) label.textContent = window.Theme.strings.updateItem;
      });

      window.Theme.on('cart:added', () => {
        changeLine(editKey, 0).catch(() => {});
      });
    }
  }

  /**
   * Rehydrates personalisation inputs from `p[…]` params so Edit returns the
   * customer to exactly what they had, rather than a blank form.
   */
  function hydrateFromUrl() {
    const params = new URLSearchParams(window.location.search);
    let hydrated = false;

    params.forEach((value, key) => {
      const match = key.match(/^p\[(.+)\]$/);
      if (!match) return;

      const input = document.querySelector(`[name="properties[${CSS.escape(match[1])}]"]`);
      if (!input || input.type === 'hidden') return;

      input.value = value;
      input.dispatchEvent(new Event('input', { bubbles: true }));
      hydrated = true;
    });

    // The spelling confirmation is deliberately NOT restored. The customer is
    // editing precisely because something may be wrong, so they must confirm
    // the new spelling rather than inherit their previous confirmation.
    if (hydrated) {
      document.querySelectorAll('[data-personalization-confirm]').forEach((box) => {
        box.checked = false;
      });
    }
  }

  if (window.location.search.includes('p[')) {
    // Custom elements upgrade on the same tick as this deferred script, so
    // waiting a frame guarantees the personalisation component is listening.
    requestAnimationFrame(hydrateFromUrl);
  }
})();
